import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Armchair, MapPin } from 'lucide-react';
import type { PublicSeatInfo } from '@peles/shared';
import { getPublicSeat } from '../api/publicRsvp';

/**
 * Public seat finder at /rsvp/:token/seat — "You are at Table 14". Gated
 * server-side by the campaign's showSeatFinder setting (unknown/ungated
 * tokens get a generic not-found).
 */
export function SeatFinderPage() {
  const { token } = useParams<{ token: string }>();
  const [seat, setSeat] = useState<PublicSeatInfo | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    getPublicSeat(token)
      .then((info) => {
        if (!cancelled) setSeat(info);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
        {loading ? (
          <div className="animate-pulse space-y-3" data-testid="seat-loading">
            <div className="h-6 bg-gray-100 rounded" />
            <div className="h-10 bg-gray-100 rounded" />
          </div>
        ) : error || !seat ? (
          <>
            <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <h1 className="text-lg font-semibold text-gray-900">Seat lookup unavailable</h1>
            <p className="mt-1 text-sm text-gray-500">
              This link is invalid or seating has not been published yet.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-500">{seat.eventName}</p>
            <h1 className="mt-1 text-xl font-semibold text-gray-900">Welcome, {seat.guestName}!</h1>
            {seat.assigned ? (
              <div className="mt-6">
                <Armchair className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                <div className="text-4xl font-bold text-gray-900">Table {seat.tableNumber}</div>
                {seat.tableLabel && (
                  <div className="mt-1 text-sm text-gray-600">{seat.tableLabel}</div>
                )}
                {seat.seatNumber != null && (
                  <div className="mt-1 text-sm text-gray-500">Seat {seat.seatNumber}</div>
                )}
              </div>
            ) : (
              <p className="mt-6 text-sm text-gray-500">
                Your seat has not been assigned yet — please check back later or ask at the welcome
                desk.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default SeatFinderPage;
